import type { Ref } from "react";
import { cloneElement } from "react";

import Carousel from "@/design-system/carousel";
import type { IViewportWrapperRequiredProps } from "@/src/master/ViewportInjector";
import ViewportWrapper from "@/src/master/ViewportInjector";
import { defaultAttributes } from "@Components/Component";
import type { IComponentAjax, IComponentAjaxState } from "@Components/ComponentAjax";
import { ComponentAjax } from "@Components/ComponentAjax";
import type { IMapMarker } from "@Components/Map";
import { Device } from "@Components/Modules/ViewportResize";
import { Panel } from "@Components/Panel";
import * as stylex from "@stylexjs/stylex";

import { type QuerySmMax, type QueryXsMax } from "./../../styles/tokens.stylex";
import type { IShoppingMallTile } from "./ShoppingMallTile";
import { ShoppingMallTile } from "./ShoppingMallTile";

// stylex styles are now used instead of CSS modules

export interface IShoppingMallsObserved extends IComponentAjax<IShoppingMallsObservedResult> {
    onMapMarkerClick: (event: React.MouseEvent<HTMLDivElement>, mapMarker: IMapMarker) => void;
    noResultsMessage: string;
    panelRef?: Ref<HTMLDivElement>;
}

interface IShoppingMallsObservedState extends IComponentAjaxState {
    isLoading: boolean;
    panelTitle?: string;
    items: IShoppingMallTile[];
}

export interface IShoppingMallsObservedResult {
    panelTitle: string;
    items: IShoppingMallTile[];
}

class ShoppingMallsObserved extends ComponentAjax<
    IShoppingMallsObservedResult,
    IShoppingMallsObserved & IViewportWrapperRequiredProps,
    IShoppingMallsObservedState
> {
    private static get defaultShoppingMallTiles(): IShoppingMallTile[] {
        const tile = {
            isLoadingLayout: true,
        } as IShoppingMallTile;

        return [
            { ...tile, id: "default-1" },
            { ...tile, id: "default-2" },
        ];
    }

    private get tiles(): JSX.Element[] {
        const items = [...this.state.items];

        const wrapperElement = <div {...stylex.props(styles.wrapper)} />;

        return items.map((value) =>
            this.tileWrapper(wrapperElement, {
                ...value,
                onMapLinkClick: this.props.onMapMarkerClick,
            }),
        );
    }

    private get sliderMobile(): JSX.Element {
        return (
            <Panel title={this.state.panelTitle} titleElement="h2">
                <Carousel
                    {...stylex.props(styles.observedCarousel)}
                    showArrows={false}
                    centerMode
                    styleX={styles.carouselContainer}
                >
                    {this.tiles}
                </Carousel>
            </Panel>
        );
    }

    private get panel(): JSX.Element {
        if (this.props.device === Device.Mobile) {
            return this.sliderMobile;
        }

        let content = null;

        if (this.state.items.length === 0) {
            content = this.props.noResultsMessage;
        } else {
            content = this.tiles;
        }

        return (
            <Panel title={this.state.panelTitle} titleElement="h2">
                <div {...stylex.props(styles.shoppingMallsTable)}>{content}</div>
            </Panel>
        );
    }

    constructor(props: IShoppingMallsObserved & IViewportWrapperRequiredProps) {
        super(props);

        this.state = {
            isLoading:
                this.props.initialData && this.props.initialData.items
                    ? this.props.initialData.items.length <= 0
                    : true,
            panelTitle: this.props.initialData ? this.props.initialData.panelTitle : undefined,
            items:
                this.props.initialData && this.props.initialData.items
                    ? this.props.initialData.items.slice()
                    : ShoppingMallsObserved.defaultShoppingMallTiles,
        };
    }

    public componentDidMount() {
        this.ajaxRequest();
    }

    public render() {
        return (
            <div
                {...defaultAttributes(this.props)}
                {...stylex.props(styles.shoppingMallsObservedComponent)}
                ref={this.props.panelRef}
            >
                {this.panel}
                {this.renderAjaxErrorContainer()}
            </div>
        );
    }

    protected ajaxSuccess(response: IShoppingMallsObservedResult): void {
        if (!response || !response.items || !response.items.length || response.items.length === 0) {
            this.setState({ isLoading: false, items: [] });
            return;
        }

        this.setState({ isLoading: false, items: response.items, panelTitle: response.panelTitle });
    }

    protected ajaxFail(): boolean {
        this.setState({ isLoading: false });
        return true;
    }

    private tileWrapper(element: JSX.Element, tile: IShoppingMallTile): JSX.Element {
        return cloneElement(
            element,
            { key: tile.id },
            <ShoppingMallTile
                {...tile}
                onMapLinkClick={this.props.onMapMarkerClick}
                className={stylex.props(styles.slide).className}
            />,
        );
    }
}

const ShoppingMallsObservedConnect = (props: IShoppingMallsObserved) => (
    <ViewportWrapper Component={ShoppingMallsObserved} props={props} />
);

export { ShoppingMallsObservedConnect as ShoppingMallsObserved };

const smMax: QuerySmMax = "@media (max-width: 989px)";
const xsMax: QueryXsMax = "@media (max-width: 759px)";

const styles = stylex.create({
    shoppingMallsObservedComponent: {},
    shoppingMallsTable: {
        marginBlock: "0",
        marginInline: "-20px",
    },
    wrapper: {
        padding: "20px",
        display: { default: "inline-block", [smMax]: "block" },
        verticalAlign: "bottom",
        width: { default: "50%", [smMax]: "100%" },
    },
    observedCarousel: {
        margin: { default: "0 -20px", [xsMax]: "0 -10px" },
    },
    carouselContainer: {
        padding: { default: "0 15px", [xsMax]: "0 5px" },
    },
    slide: {
        paddingBlock: "0",
        paddingInline: "5px",
    },
});
